import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { ColDef, GridOptions } from '@ag-grid-community/core';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { AgGridReact } from '@ag-grid-community/react';
import { faCalendarDays, faFilter, faThumbsUp, faUsers } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IonButton, IonContent, IonSelect, IonSelectOption, useIonAlert, useIonToast } from '@ionic/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { entries, filter, flow, groupBy, map, size, sumBy, uniq } from 'lodash/fp';

import '../../pages/validation/Leave.scss';
import './ValidationPointage.css';

import { useCompanies, useEmployees, useLeaves } from '../../hooks/useSwr';
import { displayMatrix } from '../../pages/validation/ChooseLeaveState';
import { Leave, OdooEmployee } from '../../types/models';
import { defaultCatchOdooError, loadingAtom, usersAtom } from '../../utils/Global';
import { dateFromOdooToFrench, nowToOdooDatetime } from '../../utils/date';
import odoo from '../../utils/odoo';
import { LeaveState, getMetaData } from '../../utils/odooUtils';
import { StatusBadge } from './ValidationPointage';
import { WebHeader } from './WebHeader';

export const ValidationLeave: React.FC = () => {
    const [presentToast] = useIonToast();
    const [validationConfirmAlert] = useIonAlert();
    const { state } = useParams<{ state?: LeaveState }>();
    const gridRef = useRef<AgGridReact>(null);

    const [selectedStates, setSelectedStates] = useState<LeaveState[]>([
        LeaveState.WaitValidation,
        LeaveState.AdherentValidation,
        LeaveState.AdherentRefusal,
        LeaveState.GeiqValidation,
        LeaveState.GeiqRefusal,
        LeaveState.Canceled,
    ]);

    // If state is provided via URL params, use that instead
    const activeStates = state && Object.values(LeaveState).includes(state) ? [state] : selectedStates;

    const { standardizedOdooLeaves: leaves, mutate } = useLeaves([['state', 'in', activeStates]]);
    const title = state ? displayMatrix[state]?.title || 'Validation Congés' : 'Validation des congés';

    type ModalEventType = { adherent_note: string };

    const { mainUser } = useAtomValue(usersAtom);
    const { normalize: employees, data: employeesData } = useEmployees();
    const { normalize: companies } = useCompanies();
    const [selectedLeaves, setSelectedLeaves] = useState<Leave[]>([]);
    const setLoading = useSetAtom(loadingAtom);

    const availableCompanies = flow(map('company_id[0]'), uniq)(employeesData) as number[];
    const filteredCompanies = filter((company) => availableCompanies.includes(company.id), companies);

    if (!employees) {
        return null;
    }

    const checkStateForBtn = [LeaveState.WaitValidation];

    // Helper function for date column sorting
    const dateComparator = (valueA: any, valueB: any) => {
        // Sort by the raw date values, not the formatted strings
        if (!valueA && !valueB) return 0;
        if (!valueA) return -1;
        if (!valueB) return 1;

        // Convert to timestamps for comparison
        const dateA = valueA.toMillis ? valueA.toMillis() : new Date(valueA).getTime();
        const dateB = valueB.toMillis ? valueB.toMillis() : new Date(valueB).getTime();

        return dateA - dateB;
    };

    // Shared date formatter function to prevent code duplication
    const formatLeaveDate = (params: any, isStartDate: boolean) => {
        if (!params.value || !params.data) return '';

        const leave = params.data as Leave;
        const dateStr = params.value.toISODate() ? dateFromOdooToFrench(params.value.toISODate()) : '';

        // For half_day mode, start shows the half_day info, end shows '-'
        if (leave.mode === 'half_day') {
            if (isStartDate) {
                const halfDayLabel = leave.half_day === 'morning' ? 'Matin' : 'Après-midi';
                return `${dateStr} (${halfDayLabel})`;
            } else {
                return '-';
            }
        }

        // For advanced mode, show the appropriate half_day info
        if (leave.mode === 'advanced') {
            const halfDayField = isStartDate ? 'start_half_day' : 'end_half_day';
            const halfDayLabel = leave[halfDayField] === 'morning' ? 'Matin' : 'Après-midi';
            return `${dateStr} (${halfDayLabel})`;
        }

        // For full_day mode, just return the date
        return dateStr;
    };

    // Column definitions for ag-grid
    const columnDefs = useMemo<ColDef<Leave>[]>(
        () => [
            {
                headerName: 'Employé',
                field: 'employee_id',
                filter: true,
                pinned: 'left',
                valueFormatter: (params: any) => {
                    const employee = employeesData?.find((emp: OdooEmployee) => emp.id === params.value);
                    return employee?.name || 'N/A';
                },
                cellStyle: {
                    textAlign: 'left',
                },
                minWidth: 150,
                initialWidth: 250,
            },
            {
                headerName: 'Type',
                field: 'type',
                flex: 1,
                valueFormatter: (params: any) => {
                    const typeMap: { [key: string]: string } = {
                        paid_leave: 'CP',
                        rtt: 'RTT',
                        unpaid_leave: 'Congé sans solde',
                        other: 'Autre',
                    };
                    return typeMap[params.value] || params.value;
                },
            },
            {
                headerName: 'Date début',
                field: 'date_start',
                flex: 2,
                valueFormatter: (params) => formatLeaveDate(params, true),
                comparator: dateComparator,
                sort: 'desc',
            },
            {
                headerName: 'Date fin',
                field: 'date_end',
                flex: 2,
                valueFormatter: (params) => formatLeaveDate(params, false),
                comparator: dateComparator,
            },
            {
                headerName: 'Notes',
                children: [
                    {
                        headerName: 'Emp.',
                        field: 'employee_note',
                        flex: 2,
                        cellStyle: { textAlign: 'left' },
                        tooltipField: 'employee_note',
                        valueGetter: (params: any) => {
                            return params.data.employee_note === false ? '' : params.data.employee_note || '';
                        },
                    },
                    {
                        headerName: 'Adhérent',
                        field: 'adherent_note',
                        flex: 2,
                        cellStyle: { textAlign: 'left' },
                        tooltipField: 'adherent_note',
                        editable: (params: any) => checkStateForBtn.includes(params.data.state),
                        cellEditor: 'agLargeTextCellEditor',
                        cellEditorPopup: true,
                        valueGetter: (params: any) => {
                            return params.data.adherent_note === false ? '' : params.data.adherent_note || '';
                        },
                    },
                ],
            },
            {
                headerName: 'Status',
                field: 'state',
                width: 140,
                pinned: 'right',
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px',
                }),
                cellRenderer: (params: any) => {
                    const orangeClassName = 'bg-orange-50 text-orange-700 border-orange-200';
                    const greenClassName = 'bg-green-50 text-green-700 border-green-200';
                    const redClassName = 'bg-red-50 text-red-700 border-red-200';
                    const grayClassName = 'bg-gray-50 text-gray-700 border-gray-200';

                    return (
                        <StatusBadge
                            state={params.value as LeaveState}
                            stateConfig={{
                                [LeaveState.WaitValidation]: {
                                    label: 'À valider',
                                    className: orangeClassName,
                                },
                                [LeaveState.AdherentValidation]: {
                                    label: 'Validé',
                                    className: greenClassName,
                                },
                                [LeaveState.AdherentRefusal]: {
                                    label: 'Refusé',
                                    className: redClassName,
                                },
                                [LeaveState.GeiqValidation]: {
                                    label: 'Validé GEIQ',
                                    className: greenClassName,
                                },
                                [LeaveState.GeiqRefusal]: {
                                    label: 'Refusé GEIQ',
                                    className: redClassName,
                                },
                                [LeaveState.Canceled]: {
                                    label: 'Annulé',
                                    className: grayClassName,
                                },
                            }}
                        />
                    );
                },
            },
        ],
        [employeesData, checkStateForBtn],
    );

    // Default column configuration
    const defaultColDef = useMemo(
        () => ({
            sortable: true,
            filter: false,
            resizable: true,
            minWidth: 100,
            cellStyle: { textAlign: 'center' },
        }),
        [],
    );

    // Grid options
    const gridOptions = useMemo<GridOptions>(
        () => ({
            rowSelection: {
                headerCheckbox: true,
                mode: 'multiRow',
                isRowSelectable: (node) => checkStateForBtn.includes(node.data?.state),
            },
            pagination: true,
            paginationPageSize: 50,
            paginationPageSizeSelector: [50, 100, 200],
            localeText: AG_GRID_LOCALE_FR,
            selectionColumnDef: {
                pinned: 'left',
            },
        }),
        [checkStateForBtn],
    );

    const onRowClicked = useCallback((event: any) => {
        const {
            event: { target },
            node,
        } = event;

        if (target.closest('[role="gridcell"]').getAttribute('col-id') == 'ag-Grid-ControlsColumn') {
            node.setSelected(!node.isSelected());
            return;
        }
    }, []);

    const getLeaveDetails = useCallback(() => {
        const leavesToValidate = filter((leave) => leave.state === LeaveState.WaitValidation, leaves);
        const totalEmployees = size(groupBy('employee_id', leavesToValidate));
        const totalDays = sumBy((leave) => {
            if ((!leave.date_start && !leave.date_end) || !leave.date_start) {
                return 0;
            }

            if (!leave.date_end) {
                return 0.5;
            }

            const diffInDays = leave.date_end.diff(leave.date_start, 'days').days;
            return leave.mode === 'half_day' ? diffInDays / 2 : diffInDays;
        }, leavesToValidate);

        return {
            hasData: leavesToValidate.length > 0,
            count: leavesToValidate.length,
            employees: totalEmployees,
            totalDays: Math.round(totalDays * 2) / 2, // Round to nearest 0.5
        };
    }, [leaves]);

    const handleCellValueChanged = useCallback(
        async (event: any) => {
            const { data, colDef, newValue, oldValue } = event;

            if (newValue === oldValue) return;

            const field = colDef.field;
            const leaveId = data.id;

            try {
                setLoading(true);
                await odoo.write('hr.madper.app.leave', leaveId, {
                    [field]: newValue,
                });
                mutate();
                presentToast({
                    message: 'Modification enregistrée avec succès',
                    duration: 2000,
                    color: 'success',
                });
            } catch (e) {
                // Revert the cell value on error
                if (gridRef.current) {
                    const rowNode = gridRef.current.api.getRowNode(leaveId.toString());
                    if (rowNode) {
                        rowNode.setDataValue(field, oldValue);
                    }
                }
                defaultCatchOdooError(e, presentToast);
            } finally {
                setLoading(false);
            }
        },
        [mutate, presentToast, setLoading],
    );

    const handleValidation = useCallback(
        async (refuse: boolean) => {
            if (!gridRef.current) {
                console.error('gridRef.current is null');
                return;
            }

            const selectedRows = gridRef.current.api.getSelectedRows();
            if (!selectedRows.length) return;

            validationConfirmAlert({
                header: `Voulez-vous ${refuse ? 'refuser' : 'accepter'} les congés sélectionnés ?`,
                buttons: [
                    {
                        text: 'Annuler',
                        role: 'cancel',
                    },
                    {
                        text: refuse ? 'Refuser' : 'Valider',
                        role: 'confirm',
                        cssClass: refuse ? 'refuse-button' : 'accept-button',
                        handler: async (ev: ModalEventType) => {
                            try {
                                setLoading(true);
                                const metadata = await getMetaData(false);
                                await Promise.all(
                                    selectedRows.map((leave: Leave) =>
                                        odoo.write('hr.madper.app.leave', leave.id, {
                                            user_adherent_id: mainUser!.uid,
                                            user_adherent_validation_date: nowToOdooDatetime(),
                                            refuse,
                                            metadata: JSON.stringify(metadata),
                                        }),
                                    ),
                                );
                                mutate();
                                gridRef.current?.api.deselectAll();
                            } catch (e) {
                                defaultCatchOdooError(e, presentToast);
                            } finally {
                                setLoading(false);
                            }
                        },
                    },
                ],
            });
        },
        [gridRef, mainUser, presentToast, validationConfirmAlert, mutate, setLoading],
    );

    const leaveDetails = getLeaveDetails();

    return (
        <>
            <WebHeader title={title} />
            <IonContent className="ion-padding bg-slate-100">
                <div style={{ height: 'calc(100% - 56px)', display: 'flex', flexDirection: 'column' }}>
                    <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Congés à valider</h3>
                                </div>
                                <div className="flex items-center justify-center space-x-6 mt-2">
                                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-md">
                                        <FontAwesomeIcon icon={faUsers} className="text-blue-600" />
                                        <span className="text-sm font-medium text-gray-700">
                                            {leaveDetails.hasData
                                                ? `${leaveDetails.count} congés · ${leaveDetails.employees} employés`
                                                : '-'}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-md">
                                        <FontAwesomeIcon icon={faCalendarDays} className="text-green-600" />
                                        <span className="text-sm font-medium text-gray-700">
                                            {leaveDetails.hasData
                                                ? `${leaveDetails.totalDays % 1 === 0 ? leaveDetails.totalDays : leaveDetails.totalDays.toFixed(1)} jours`
                                                : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">Filtrer par statut :</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <IonSelect
                                        className="filter-select"
                                        style={{
                                            '--border-radius': '8px',
                                            '--background': '#f8fafc',
                                            '--color': '#374151',
                                            '--padding-start': '14px',
                                            '--padding-end': '14px',
                                            minWidth: '280px',
                                            maxWidth: '400px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                        }}
                                        interface="alert"
                                        interfaceOptions={{
                                            header: 'Sélectionner les statuts à afficher',
                                        }}
                                        value={selectedStates}
                                        onIonChange={(e) => {
                                            setSelectedStates(e.detail.value);
                                        }}
                                        multiple={true}
                                        cancelText="Annuler"
                                        okText="Valider"
                                        placeholder="Aucun statut sélectionné"
                                    >
                                        {map(
                                            ([state, data]: [LeaveState, any]) => (
                                                <IonSelectOption key={state} value={state}>
                                                    {data.title}
                                                </IonSelectOption>
                                            ),
                                            entries(displayMatrix),
                                        )}
                                    </IonSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="validation-pointage-grid ag-theme-alpine" style={{ flex: 1, width: '100%' }}>
                        <AgGridReact
                            rowData={leaves}
                            columnDefs={columnDefs}
                            tooltipShowDelay={0}
                            tooltipHideDelay={500}
                            defaultColDef={defaultColDef}
                            gridOptions={gridOptions}
                            onRowClicked={onRowClicked}
                            onCellValueChanged={handleCellValueChanged}
                            enableCellTextSelection={true}
                            ref={gridRef}
                            getRowId={(params) => params.data.id.toString()}
                            loading={false}
                            stopEditingWhenCellsLoseFocus={true}
                            onSelectionChanged={() => {
                                setSelectedLeaves(gridRef.current?.api?.getSelectedRows() || []);
                            }}
                        />
                    </div>
                    <div className="madper-button flex space-x-4">
                        <IonButton
                            shape="round"
                            className="mt-6 mb-0"
                            onClick={() => handleValidation(false)}
                            disabled={size(filter({ state: LeaveState.WaitValidation }, selectedLeaves)) < 1}
                            color="success"
                        >
                            {size(filter({ state: LeaveState.WaitValidation }, selectedLeaves)) > 1
                                ? 'Valider les congés sélectionnés'
                                : 'Valider le congé sélectionné'}
                        </IonButton>
                        <IonButton
                            shape="round"
                            className="mt-6 mb-0"
                            onClick={() => handleValidation(true)}
                            disabled={size(filter({ state: LeaveState.WaitValidation }, selectedLeaves)) < 1}
                            color="danger"
                        >
                            {size(filter({ state: LeaveState.WaitValidation }, selectedLeaves)) > 1
                                ? 'Refuser les congés sélectionnés'
                                : 'Refuser le congé sélectionné'}
                        </IonButton>
                    </div>
                </div>
            </IonContent>
        </>
    );
};

export default ValidationLeave;
